/**
 * Format number with PT locale. By default, applies magnitude-based rule:
 * - |num| ≥ 1000 → 0 decimals
 * - 1 ≤ |num| < 1000 → 2 decimals
 * - |num| < 1 → up to 3 decimals (no unnecessary trailing zeros)
 * If `decimals` is provided, it forces fixed decimals.
 * @param num Number to format
 * @param decimals Optional fixed decimal places. If omitted, uses dynamic rule.
 * @param maxDynamicDecimals Max decimals when using dynamic rule (default: 3)
 * @returns Formatted number string
 */
export const formatNumber = (
  num: number | string,
  decimals?: number,
  maxDynamicDecimals: number = 3
): string => {
  const maybeNumber = typeof num === "string" ? Number(num) : num;
  const value = typeof maybeNumber === "number" && isFinite(maybeNumber) ? maybeNumber : 0;

  if (typeof decimals === "number") {
    return value.toLocaleString("pt-PT", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }

  const absValue = Math.abs(value);
  if (absValue >= 1000) {
    return value.toLocaleString("pt-PT", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }
  if (absValue >= 1) {
    // Exactly 2 decimals for mid-range values
    return value.toLocaleString("pt-PT", {
      minimumFractionDigits: Math.min(2, maxDynamicDecimals),
      maximumFractionDigits: Math.min(2, maxDynamicDecimals),
    });
  }
  // Values below 1: up to maxDynamicDecimals, without padding zeros unnecessarily
  return value.toLocaleString("pt-PT", {
    minimumFractionDigits: 0,
    maximumFractionDigits: Math.min(3, maxDynamicDecimals),
  });
};

/**
 * Format number as integer (no decimal places) with thousands separator using PT locale
 * @param num Number to format
 * @returns Formatted number string
 */
export const formatInteger = (num: number | string): string => {
  const maybeNumber = typeof num === "string" ? Number(num) : num;
  const value = typeof maybeNumber === "number" && isFinite(maybeNumber) ? maybeNumber : 0;
  return Math.round(value).toLocaleString("pt-PT", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

/**
 * Format currency values properly, avoiding leading zeros
 * @param num Number to format
 * @param decimals Number of decimal places (default: 2)
 * @returns Formatted currency string
 */
export const formatCurrency = (num: number | string, decimals: number = 2): string => {
  const maybeNumber = typeof num === "string" ? Number(num) : num;
  const value = typeof maybeNumber === "number" && isFinite(maybeNumber) ? maybeNumber : 0;
  // Ensure the number is properly rounded to avoid floating point precision issues
  const roundedNum = Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
  
  return roundedNum.toLocaleString("pt-PT", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Format number using a magnitude-based decimal rule (PT locale):
 * - |num| >= 1000  → 0 decimal places
 * - 1 <= |num| < 1000 → 2 decimal places
 * - |num| < 1 → up to 3 decimal places
 * The number of decimals is also capped by maxDecimals.
 * @param num Number to format
 * @param maxDecimals Maximum number of decimal places to show (default: 3)
 * @returns Formatted number string
 */
export const formatNumberDynamic = (num: number | string, maxDecimals: number = 3): string => {
  const maybeNumber = typeof num === "string" ? Number(num) : num;
  const value = typeof maybeNumber === "number" && isFinite(maybeNumber) ? maybeNumber : 0;
  const absValue = Math.abs(value);

  if (absValue >= 1000) {
    return value.toLocaleString("pt-PT", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  if (absValue >= 1) {
    return value.toLocaleString("pt-PT", {
      minimumFractionDigits: Math.min(2, maxDecimals),
      maximumFractionDigits: Math.min(2, maxDecimals),
    });
  }

  return value.toLocaleString("pt-PT", {
    minimumFractionDigits: 0,
    maximumFractionDigits: Math.min(3, maxDecimals),
  });
};
